/**
 * @file apps/web/src/pages/LoginPage.tsx
 * @description Login page for the SI firm workspace.
 * Real credentials go through supabase.auth.signInWithPassword(), then
 * GET /auth/me hydrates the full AuthUser from the returned access token
 * (Supabase's own JWT carries no Veridox-specific claims — tenantId,
 * projectId, role, userType all come from that call). Client users
 * (userType='client') are redirected to /portal/dashboard; SI users to
 * /dashboard.
 *
 * The dev quick-login panel below (DEV_MODE only) bypasses Supabase
 * entirely — it calls POST /auth/dev-login + /auth/dev-token directly,
 * same as before, so local dev/testing doesn't need a real Supabase
 * project.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, LogIn, ChevronDown } from 'lucide-react';
import { useAuth, type AuthUser } from '../lib/auth.js';
import { usePortalAuth, DEV_PORTAL_USERS, type PortalUser } from '../lib/portalAuth.js';
import { authApi } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';

const DEV_PASSWORD = 'password123';

// ---------------------------------------------------------------------------
// Dev seed users (for quick-login panel convenience)
// ---------------------------------------------------------------------------

const DEV_USERS: { email: string; label: string; role: string }[] = [
  { email: 'sarah@clarity.com',  label: 'Sarah (SI Admin)',        role: 'owner' },
  { email: 'james@clarity.com',  label: 'James (Project Manager)', role: 'lead' },
  { email: 'mike@clarity.com',   label: 'Mike (Project Team)',     role: 'consultant' },
  { email: 'tester@clarity.com', label: 'Alex (SI Tester)',        role: 'consultant' },
];

const DEV_CLIENT_USERS: { email: string; label: string; role: string }[] = [
  { email: 'jthornton@acme.com', label: 'James Thornton (Client Admin)', role: 'client_admin' },
  { email: 'lchen@acme.com',     label: 'Linda Chen (Client Admin)',     role: 'client_admin' },
];

// ---------------------------------------------------------------------------
// LoginPage
// ---------------------------------------------------------------------------

export function LoginPage() {
  const { isAuthenticated, setUser } = useAuth();
  const { setUser: setPortalUser }   = usePortalAuth();
  const navigate = useNavigate();

  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);

  // Already logged in — bounce to dashboard
  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) return null;

  // Populates both auth contexts and redirects, given a resolved AuthUser
  // and (for client users) a token to use in the portal context.
  const applyUserAndRedirect = (user: AuthUser, portalProfile: Omit<PortalUser, 'token'> | undefined) => {
    setUser(user);

    if (user.userType === 'client') {
      // Also populate the portal auth context so PortalShell recognises the session
      if (portalProfile) {
        setPortalUser({ ...portalProfile, token: user.token });
      }
      navigate('/portal/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  // Real credentials — Supabase is the source of truth. GET /auth/me
  // hydrates everything AuthUser needs from the access token Supabase hands
  // back; there are no Veridox-specific claims on the Supabase JWT itself.
  const doLogin = async (loginEmail: string, loginPassword: string) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });
      if (authError || !data.session) {
        throw new Error(authError?.message ?? 'Invalid email or password.');
      }

      const accessToken = data.session.access_token;
      const me = await authApi.me({ Authorization: `Bearer ${accessToken}` });

      const user: AuthUser = {
        userId:     me.id,
        tenantId:   me.tenantId,
        email:      me.email,
        fullName:   me.fullName ?? me.email,
        projectId:  me.projectId,
        token:      accessToken,
        userType:   me.userType,
        role:       me.role,
        tenantRole: me.tenantRole,
      };

      // DEV_PORTAL_USERS only covers the hardcoded local seed accounts —
      // a real client user authenticated via Supabase won't be in it.
      // clientName has no other data source right now (GET /auth/me
      // doesn't return it either); this is the same gap flagged in
      // c1b40a4 for the dev login path, just now also hit by real logins.
      const portalProfile = DEV_PORTAL_USERS.find(
        (u) => u.email.toLowerCase() === loginEmail.trim().toLowerCase(),
      ) ?? (user.userType === 'client'
        ? { userId: user.userId, tenantId: user.tenantId, email: user.email, fullName: user.fullName,
            projectId: user.projectId, role: user.role ?? 'client_user', clientName: '' }
        : undefined);

      applyUserAndRedirect(user, portalProfile);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  // Dev quick-login (DEV_MODE only) — bypasses Supabase entirely via the
  // API's own dev-only routes: POST /auth/dev-login validates the password
  // and looks up the seed profile, POST /auth/dev-token mints a session JWT
  // for it (see apps/api/src/plugins/auth.ts's signDevJwt).
  const doDevLogin = async (loginEmail: string) => {
    setLoading(true);
    setError('');
    try {
      const profile = await authApi.devLogin(loginEmail, DEV_PASSWORD);
      const { token } = await authApi.devToken({ userId: profile.userId, tenantId: profile.tenantId });
      const userType: 'si' | 'client' =
        (profile.role === 'client_admin' || profile.role === 'client_user') ? 'client' : 'si';

      const user: AuthUser = {
        userId:     profile.userId,
        tenantId:   profile.tenantId,
        email:      profile.email,
        fullName:   profile.fullName,
        projectId:  profile.projectId,
        token,
        userType,
        role:       profile.role,
        tenantRole: profile.tenantRole,
      };

      const portalProfile = DEV_PORTAL_USERS.find(
        (u) => u.email.toLowerCase() === loginEmail.trim().toLowerCase(),
      );

      applyUserAndRedirect(user, portalProfile);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Dev login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Email and password are required.');
      return;
    }
    void doLogin(email.trim(), password);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--gray)' }}
    >
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div
            className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-mono font-bold"
            style={{ backgroundColor: 'var(--blue)' }}
          >
            V
          </div>
          <span className="font-display text-2xl font-bold tracking-tight" style={{ color: 'var(--ink)' }}>
            Veridox
          </span>
        </div>

        <div className="card p-6 space-y-5">
          <div>
            <h1 className="font-display text-lg font-semibold text-ink">Sign in</h1>
            <p className="text-sm text-muted mt-0.5">Enter your work email to continue.</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
              <AlertCircle size={14} className="shrink-0" /> {error}
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="label mb-1.5">Work Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourfirm.com"
                className="input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="label mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center"
            >
              <LogIn size={15} />
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          {/* Dev quick-login panel — stripped from production builds entirely:
              Vite replaces import.meta.env.DEV with a literal `false` at
              build time, so esbuild's dead-code elimination drops this
              whole branch (JSX and the DEV_USERS/DEV_CLIENT_USERS arrays
              together) rather than just hiding it, keeping real seed-user
              emails out of the production bundle. */}
          {import.meta.env.DEV && (
            <div className="border-t border-[rgba(8,17,31,0.06)] pt-4">
              <button
                onClick={() => setShowDevPanel((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-mono text-muted hover:text-ink transition-colors w-full"
              >
                <ChevronDown
                  size={12}
                  className={['transition-transform', showDevPanel ? 'rotate-180' : ''].join(' ')}
                />
                Dev quick-login (seed users)
              </button>

              {showDevPanel && (
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="text-[10px] font-mono text-muted/60 mb-1.5 uppercase tracking-wider">SI Team</p>
                    <div className="space-y-1">
                      {DEV_USERS.map((u) => (
                        <button
                          key={u.email}
                          onClick={() => { setEmail(u.email); setPassword(DEV_PASSWORD); void doDevLogin(u.email); }}
                          className={[
                            'w-full text-left p-2.5 rounded-lg border text-sm transition-colors',
                            email === u.email
                              ? 'border-blue bg-sky text-ink'
                              : 'border-[rgba(8,17,31,0.08)] hover:border-blue/30 hover:bg-sky/20 text-ink',
                          ].join(' ')}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{u.label}</span>
                            <span className="text-[10px] font-mono text-muted">{u.role}</span>
                          </div>
                          <p className="text-[11px] font-mono text-muted mt-0.5">{u.email}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-mono text-muted/60 mb-1.5 uppercase tracking-wider">Client Portal</p>
                    <div className="space-y-1">
                      {DEV_CLIENT_USERS.map((u) => (
                        <button
                          key={u.email}
                          onClick={() => { setEmail(u.email); setPassword(DEV_PASSWORD); void doDevLogin(u.email); }}
                          className={[
                            'w-full text-left p-2.5 rounded-lg border text-sm transition-colors',
                            email === u.email
                              ? 'border-blue bg-sky text-ink'
                              : 'border-[rgba(8,17,31,0.08)] hover:border-blue/30 hover:bg-sky/20 text-ink',
                          ].join(' ')}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{u.label}</span>
                            <span className="text-[10px] font-mono text-muted">{u.role}</span>
                          </div>
                          <p className="text-[11px] font-mono text-muted mt-0.5">{u.email}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] font-mono text-muted/50">Password: password123</p>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-[11px] font-mono text-muted mt-4">
          Veridox · ERP Implementation Platform
        </p>
      </div>
    </div>
  );
}
